import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api';

// ==================== Initial State ====================
const initialState = {
    referralForms: [],
    currentReferralForm: null,
    isLoading: false,
    error: null,
};

// ==================== Async Thunks ====================

// Fetch all Referral Forms
export const fetchReferralForms = createAsyncThunk(
    'referralForms/fetchReferralForms',
    async (_, { rejectWithValue }) => {
        try {
            const response = await api.get('/referral-forms');
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch Referral forms'
            );
        }
    }
);

// Fetch a single Referral Form
export const fetchReferralForm = createAsyncThunk(
    'referralForms/fetchReferralForm',
    async (id, { rejectWithValue }) => {
        try {
            const response = await api.get(`/referral-forms/${id}`);
            return response.data.data;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to fetch Referral form'
            );
        }
    }
);

// Create a new Referral Form
export const createReferralForm = createAsyncThunk(
    'referralForms/createReferralForm',
    async (referralData, { rejectWithValue }) => {
        try {
            const response = await api.post('/referral-forms', referralData);
            return response.data.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to create Referral form'
            );
        }
    }
);

// Update an existing Referral Form (by ID)
export const updateReferralForm = createAsyncThunk(
    'referralForms/updateReferralForm',
    async ({ case_id, referralData }, { rejectWithValue }) => {
        try {
            const response = await api.put(`/referral-forms/case/${case_id}`, referralData);
            return response.data.data ?? response.data;
        } catch (error) {
            if (error.response?.status === 422) {
                return rejectWithValue(error.response.data.errors);
            }
            return rejectWithValue(
                error.response?.data?.error || 'Failed to update Referral form'
            );
        }
    }
);

// Delete a Referral Form
export const deleteReferralForm = createAsyncThunk(
    'referralForms/deleteReferralForm',
    async (id, { rejectWithValue }) => {
        try {
            await api.delete(`/referral-forms/${id}`);
            return id;
        } catch (error) {
            return rejectWithValue(
                error.response?.data?.error || 'Failed to delete Referral form'
            );
        }
    }
);

// ==================== Slice ====================
const referralFormsSlice = createSlice({
    name: 'referralForms',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        setCurrentReferralForm: (state, action) => {
            state.currentReferralForm = action.payload;
        },
        clearCurrentReferralForm: (state) => {
            state.currentReferralForm = null;
        },
    },
    extraReducers: (builder) => {
        builder
            // FETCH ALL
            .addCase(fetchReferralForms.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchReferralForms.fulfilled, (state, action) => {
                state.isLoading = false;
                state.referralForms = action.payload;
            })
            .addCase(fetchReferralForms.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // FETCH SINGLE
            .addCase(fetchReferralForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchReferralForm.fulfilled, (state, action) => {
                state.isLoading = false;
                state.currentReferralForm = action.payload;
            })
            .addCase(fetchReferralForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // CREATE
            .addCase(createReferralForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(createReferralForm.fulfilled, (state, action) => {
                state.isLoading = false;
                state.referralForms.unshift(action.payload);
            })
            .addCase(createReferralForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // UPDATE
            .addCase(updateReferralForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(updateReferralForm.fulfilled, (state, action) => {
                state.isLoading = false;
                const updated = action.payload.data ?? action.payload;

                const index = state.referralForms.findIndex(
                    (d) => d.id === updated.id
                );
                if (index !== -1) {
                    state.referralForms[index] = updated;
                }

                if (state.currentReferralForm?.id === updated.id) {
                    state.currentReferralForm = updated;
                }
            })
            .addCase(updateReferralForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })

            // DELETE
            .addCase(deleteReferralForm.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(deleteReferralForm.fulfilled, (state, action) => {
                state.isLoading = false;
                state.referralForms = state.referralForms.filter(
                    (d) => d.id !== action.payload
                );

                if (state.currentReferralForm?.id === action.payload) {
                    state.currentReferralForm = null;
                }
            })
            .addCase(deleteReferralForm.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            });
    },
});

export const {
    clearError,
    setCurrentReferralForm,
    clearCurrentReferralForm,
} = referralFormsSlice.actions;

export default referralFormsSlice.reducer;
